import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as filesApi from './api'
import type { UpdateFileMetadataRequest } from './types'

export const fileKeys = {
  list: (contractId: number) => ['contracts', contractId, 'files'] as const,
}

export function useContractFiles(contractId: number | null | undefined) {
  return useQuery({
    queryKey: fileKeys.list(contractId ?? -1),
    queryFn: () => filesApi.listFiles(contractId as number),
    enabled: typeof contractId === 'number' && contractId > 0,
  })
}

export function useUploadFile(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { file: File; documentTypeId?: number | null; description?: string | null }) =>
      filesApi.uploadFile(contractId, vars.file, { documentTypeId: vars.documentTypeId, description: vars.description }),
    // onSettled (nie onSuccess): nawet gdy klient dostal blad/timeout, plik mogl sie
    // zapisac serwerowo - odswiezamy liste, zeby pole/zakladka Pliki to zobaczyly.
    onSettled: () => qc.invalidateQueries({ queryKey: fileKeys.list(contractId) }),
  })
}

export function useDeleteFile(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: number) => filesApi.deleteFile(contractId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.list(contractId) }),
  })
}

export function useUpdateFileMetadata(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { fileId: number; payload: UpdateFileMetadataRequest }) =>
      filesApi.updateMetadata(contractId, vars.fileId, vars.payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.list(contractId) }),
  })
}

export async function downloadFile(contractId: number, fileId: number): Promise<void> {
  const { url } = await filesApi.getDownloadUrl(contractId, fileId)
  window.location.href = url
}

/**
 * Pobiera pre-signed URL do pełnego pliku. Cache 5 min - URL-e są krótkotrwałe,
 * ale nie chcemy bombardować backendu przy re-renderze. Używać dla lightboxa
 * i pobierania - dla miniatur w gridzie patrz {@link useFileThumbnailUrl}.
 */
export function useFileDownloadUrl(contractId: number, fileId: number | null | undefined) {
  return useQuery({
    queryKey: ['contracts', contractId, 'files', fileId, 'download-url'] as const,
    queryFn: () => filesApi.getDownloadUrl(contractId, fileId as number),
    enabled: typeof fileId === 'number' && fileId > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Pobiera pre-signed URL do cached miniaturki danej szerokości. Backend
 * generuje thumb on-the-fly przy pierwszym requeście i cache'uje w S3 pod
 * prefiksem {@code thumbnails/}. Cache 5 min po stronie React Query.
 */
export function useFileThumbnailUrl(
  contractId: number,
  fileId: number | null | undefined,
  width: number,
) {
  return useQuery({
    queryKey: ['contracts', contractId, 'files', fileId, 'thumbnail-url', width] as const,
    queryFn: () => filesApi.getThumbnailUrl(contractId, fileId as number, width),
    enabled: typeof fileId === 'number' && fileId > 0,
    staleTime: 5 * 60 * 1000,
  })
}
